import React, { useMemo, useState } from 'react';
import { Info } from 'lucide-react';
import { LineChart, BarChart } from '@shared/components/charts';
import { SearchableSelectFilter } from '@shared/components/filters/base/SearchableSelectFilter';
import { DATE_PRESET_OPTIONS } from '@shared/components/filters/components/date/DateFilter';
import { Card, CardContent, CardHeader } from '@shared/components/ui/card';
import { Tooltip, TooltipContent, TooltipTrigger } from '@shared/components/ui/tooltip';
import { INTERVAL_OPTIONS, COMPARE_OPTIONS } from '@shared/components/pages/home/home-page.data';
import type { Terminal } from '@features/in-stores/types/terminal.types';
import {
  TERMINAL_TRANSACTION_VOLUME_DATA,
  TERMINAL_CONNECTIVITY_UPTIME_DATA,
  TERMINAL_REVENUE_DATA,
  TERMINAL_PAYMENT_METHOD_DATA,
} from '@shared/data/mockTerminalAnalytics';

interface TerminalAnalyticsTabProps {
  terminal?: Terminal | null;
  className?: string;
}

interface ChartSection {
  id: string;
  title: string;
  value: string;
  subtitle: string;
  tooltip: string;
  chartType: 'line' | 'bar';
}

export const TerminalAnalyticsTab = React.memo<TerminalAnalyticsTabProps>(({
  className,
}) => {
  const [dateFilter, setDateFilter] = useState<string | null>('last-30-days');
  const [timeInterval, setTimeInterval] = useState<string | null>('daily');
  const [compareMode, setCompareMode] = useState<string | null>('no-comparison');

  const sections = useMemo<ChartSection[]>(() => [
    {
      id: 'transaction-volume',
      title: 'Transaction Volume',
      value: '3,247',
      subtitle: 'Last 30 days',
      tooltip: 'Total number of transactions processed by this terminal over the selected period.',
      chartType: 'line',
    },
    {
      id: 'connectivity-uptime',
      title: 'Connectivity Uptime',
      value: '99.8%',
      subtitle: 'Last 30 days',
      tooltip: 'Percentage of time the terminal maintained active network connectivity.',
      chartType: 'line',
    },
    {
      id: 'revenue',
      title: 'Weekly Revenue',
      value: '$24,567.89',
      subtitle: 'All time',
      tooltip: 'Total revenue processed through this terminal.',
      chartType: 'bar',
    },
    {
      id: 'payment-methods',
      title: 'Payment Methods',
      value: '3,247',
      subtitle: 'Total transactions',
      tooltip: 'Breakdown of payment methods used at this terminal.',
      chartType: 'bar',
    },
  ], []);

  const renderChart = (section: ChartSection) => {
    if (section.id === 'transaction-volume') {
      return (
        <div className="relative h-40">
          <LineChart
            data={TERMINAL_TRANSACTION_VOLUME_DATA}
            dataKeys={['transactions', 'successful']}
            colors={['var(--chart-2)', 'var(--chart-5)']}
            height={160}
            showGrid
            showLegend={false}
            showTooltip
          />
        </div>
      );
    }
    if (section.id === 'connectivity-uptime') {
      return (
        <div className="relative h-40">
          <LineChart
            data={TERMINAL_CONNECTIVITY_UPTIME_DATA}
            dataKeys={['uptime']}
            colors={['var(--chart-2)']}
            height={160}
            showGrid
            showLegend={false}
            showTooltip
            formatYAxis={(v) => `${v}%`}
            formatTooltip={(v) => `${v}%`}
          />
        </div>
      );
    }
    if (section.id === 'revenue') {
      return (
        <div className="relative h-40">
          <BarChart
            data={TERMINAL_REVENUE_DATA}
            dataKeys={['revenue']}
            colors={['var(--chart-2)']}
            height={160}
            showGrid
            showLegend={false}
            showTooltip
            barSize={20}
            formatYAxis={(v) => `$${(v / 100).toFixed(0)}`}
            formatTooltip={(v) => `$${(v / 100).toFixed(2)}`}
          />
        </div>
      );
    }
    if (section.id === 'payment-methods') {
      return (
        <div className="relative h-40">
          <BarChart
            data={TERMINAL_PAYMENT_METHOD_DATA}
            dataKeys={['count']}
            colors={['var(--chart-2)']}
            height={160}
            showGrid
            showLegend={false}
            showTooltip
            layout="vertical"
          />
        </div>
      );
    }
    return null;
  };

  return (
    <div className={className}>
      {/* Filters Row */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <SearchableSelectFilter
          options={DATE_PRESET_OPTIONS}
          value={dateFilter}
          onChange={setDateFilter}
          filterConfig={{ key: 'date', label: 'Date range', type: 'select' }}
          searchPlaceholder="Search..."
        />
        <SearchableSelectFilter
          options={INTERVAL_OPTIONS}
          value={timeInterval}
          onChange={setTimeInterval}
          filterConfig={{ key: 'interval', label: 'Interval', type: 'select' }}
          searchPlaceholder="Search interval..."
        />
        <SearchableSelectFilter
          options={COMPARE_OPTIONS}
          value={compareMode}
          onChange={setCompareMode}
          filterConfig={{ key: 'compare', label: 'Compare', type: 'select' }}
          searchPlaceholder="Search compare..."
        />
      </div>

      {/* Chart Cards Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {sections.map((section) => (
          <Card
            key={section.id}
            className="chart-card flex flex-col gap-4 shadow-sm overflow-hidden border-0"
          >
            <CardHeader className="chart-card-header flex flex-col items-start pb-0">
              <div className="flex items-start justify-between w-full">
                <div className="flex items-center gap-1.5">
                  <span className="chart-title">{section.title}</span>
                  <Tooltip delayDuration={200}>
                    <TooltipTrigger asChild>
                      <Info className="chart-info-icon cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs whitespace-pre-line">
                      {section.tooltip}
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
              <div className="chart-value">{section.value}</div>
              <div className="chart-subtitle">{section.subtitle}</div>
            </CardHeader>
            <CardContent className="space-y-3">
              {renderChart(section)}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
});

TerminalAnalyticsTab.displayName = 'TerminalAnalyticsTab';
